// modules/transactions/index.ts — Platik-IA
// CRUD de transacciones. Escribe en IndexedDB (inmediato) + encola sync a Sheets.

import { bus }        from '../../core/event-bus';
import { getModule }  from '../../core/registry';
import {
  getAllTransactions, putTransaction, deleteTransaction, enqueueSyncItem,
  normalizeLegacyDates,
} from '../../services/idb';
import { uuid }       from '../../shared/utils/format';
import type { TransactionsAPI, NewTransaction } from './contracts';
import type { Transaction }                     from '../../shared/types/global';

let _cache: Transaction[] = [];

async function refreshCache(): Promise<void> {
  const rows = await getAllTransactions();
  _cache = rows.map(r => ({
    id:           r.id,
    fecha:        r.fecha,
    tipo:         r.tipo,
    monto:        r.monto,
    descripcion:  r.descripcion,
    categoria:    r.categoria,
    subcategoria: r.subcategoria,
    syncStatus:   r.syncStatus,
  }));
}

export async function initTransactions(): Promise<TransactionsAPI> {
  await normalizeLegacyDates();
  await refreshCache();
  bus.emit('transactions:loaded', { transactions: _cache });
  bus.emit('transactions:ready');

  // Re-cargar cuando sync completa. Guardamos la referencia para poder removerla
  // en dispose() (una función anónima distinta no removería el listener real).
  const onSyncCompleted = async () => {
    await refreshCache();
    bus.emit('transactions:loaded', { transactions: _cache });
  };
  bus.on('sync:completed', onSyncCompleted);

  return {
    getAll: () => [..._cache],

    async save(data: NewTransaction) {
      const now = new Date().toISOString();
      const tx: Transaction = {
        id:           uuid(),
        fecha:        data.fecha ?? now,
        tipo:         data.tipo,
        monto:        data.monto,
        descripcion:  data.descripcion,
        categoria:    data.categoria,
        subcategoria: data.subcategoria,
        syncStatus:   'pending',
      };

      // 1. IndexedDB — inmediato
      await putTransaction({ ...tx, syncStatus: 'pending', syncedAt: null, createdAt: now });
      _cache.unshift(tx);
      bus.emit('transactions:saved', { transaction: tx });

      // 2. Encolar sync a Sheets
      await enqueueSyncItem({
        id:         uuid(),
        operation:  'create',
        entityType: 'transaction',
        entityId:   tx.id,
        payload:    { id: tx.id, fecha: tx.fecha, tipo: tx.tipo, monto: tx.monto, descripcion: tx.descripcion, categoria: tx.categoria, subcategoria: tx.subcategoria },
        createdAt:  now,
      });

      // 3. Flush inmediato en background (no bloquea)
      getModule<{ flush: () => Promise<void> }>('sync')?.flush().catch(console.warn);

      return tx;
    },

    async update(id, data) {
      const idx = _cache.findIndex(t => t.id === id);
      if (idx === -1) throw new Error(`Transacción ${id} no encontrada`);

      const updated: Transaction = { ..._cache[idx], ...data, syncStatus: 'pending' };
      _cache[idx] = updated;

      await putTransaction({ ...updated, syncStatus: 'pending', syncedAt: null, createdAt: updated.fecha });
      bus.emit('transactions:updated', { transaction: updated });

      await enqueueSyncItem({
        id:         uuid(),
        operation:  'update',
        entityType: 'transaction',
        entityId:   id,
        payload:    { id, fecha: updated.fecha, tipo: updated.tipo, monto: updated.monto, descripcion: updated.descripcion, categoria: updated.categoria, subcategoria: updated.subcategoria },
        createdAt:  new Date().toISOString(),
      });

      getModule<{ flush: () => Promise<void> }>('sync')?.flush().catch(console.warn);
    },

    async remove(id) {
      _cache = _cache.filter(t => t.id !== id);
      await deleteTransaction(id);
      bus.emit('transactions:deleted', { id });

      await enqueueSyncItem({
        id:         uuid(),
        operation:  'delete',
        entityType: 'transaction',
        entityId:   id,
        payload:    { id },
        createdAt:  new Date().toISOString(),
      });

      getModule<{ flush: () => Promise<void> }>('sync')?.flush().catch(console.warn);
    },

    dispose() {
      bus.off('sync:completed', onSyncCompleted);
    },
  };
}
